import { InfoCircleOutlined } from '@ant-design/icons';
import { create, useModal } from '@ebay/nice-modal-react';
import { Badge, Button, Empty, List, message, Modal, Popover, Upload } from 'antd';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import uploadIcon from '../../../assets/upload.svg';
import Img1 from '../../../assets/excel-wenxian-1.jpg';
import Img2 from '../../../assets/excel-wenxian-2.jpg';
import { baseURL } from '../../../axios/axios';
import { useDevice } from '../../../hooks/useDevice';
import { generateUUID } from '../CustomPasteModal/utils';
import LiteratureItem from '../LiteratureSearchModal/LiteratureItem';
import SelectedLiteratureItem from '../LiteratureSearchModal/SelectedLiteratureItem';
import { summaryStore } from '../store';

/**
 * 上传RIS文献组件
 */
const UploadRisModal = create(
    observer(() => {
        const modal = useModal();
        const { isMobile } = useDevice();
        const [fileList, setFileList] = useState([]);
        const [uploading, setUploading] = useState(false);
        const [parsedReferences, setParsedReferences] = useState([]);
        const [selectedItems, setSelectedItems] = useState([]);
        const [showSelected, setShowSelected] = useState(false);
        const [hasUploaded, setHasUploaded] = useState(false);
        const [refId, setRefId] = useState(null);

        // 重置状态
        useEffect(() => {
            if (modal.visible) {
                setFileList([]);
                setParsedReferences([]);
                setSelectedItems([]);
                setShowSelected(false);
                setHasUploaded(false);
                setUploading(false);
            }
        }, [modal.visible]);

        // RIS 上传配置
        const uploadProps = {
            name: 'file',
            showUploadList: false,
            accept: '.ris',
            multiple: false,
            action: `${baseURL}ai/ai/paper/upload/ref`,

            // 上传前校验
            beforeUpload: (file) => {
                const isLt50M = file.size / 1024 / 1024 < 50;
                if (!isLt50M) {
                    message.error('文件大小不能超过 50MB!');
                    return Upload.LIST_IGNORE;
                }

                // 如果已有文件，清空文件列表
                if (fileList.length > 0) {
                    setFileList([]);
                    setParsedReferences([]);
                    setSelectedItems([]);
                }

                return true;
            },

            // 上传状态改变的回调
            onChange(info) {
                const { status } = info.file;

                // 始终只保留最后一个文件
                setFileList(info.fileList.slice(-1));

                if (status === 'uploading') {
                    setUploading(true);
                    return;
                }

                if (status === 'done') {
                    setUploading(false);
                    message.success(`${info.file.name} 上传成功`);

                    // 获取服务器返回的数据
                    const response = info.file.response;
                    console.log('response', response);

                    if (response && response.code === 0 && response.data) {
                        const { refId, refs } = response.data;

                        // 为每个引用添加唯一ID
                        const refsWithIds = refs.map((ref, index) => ({
                            ...ref,
                            id: generateUUID(),
                        }));

                        // 更新本地状态
                        setParsedReferences(refsWithIds);
                        setSelectedItems(refsWithIds); // 自动全选
                        setHasUploaded(true);
                        setRefId(refId);
                    }
                } else if (status === 'error') {
                    setUploading(false);
                    message.error(`${info.file.name} 上传失败`);
                    setFileList([]);
                    setParsedReferences([]);
                    setSelectedItems([]);
                    setHasUploaded(false);
                }
            },
        };

        // 重新上传
        const handleReUpload = () => {
            setFileList([]);
            setParsedReferences([]);
            setSelectedItems([]);
            setHasUploaded(false);
            setShowSelected(false);
        };

        // 选择/取消选择文献
        const handleSelect = (item) => {
            if (!selectedItems.find((selected) => selected.id === item.id)) {
                setSelectedItems([...selectedItems, item]);
            } else {
                setSelectedItems(selectedItems.filter((selected) => selected.id !== item.id));
            }
        };

        // 从已选列表中移除
        const handleRemove = (item) => {
            setSelectedItems(selectedItems.filter((selected) => selected.id !== item.id));
        };

        // 确认使用文献
        const handleOk = () => {
            if (refId) {
                summaryStore.setRefFileId(refId);
            }
            modal.resolve(selectedItems);
            modal.hide();
        };

        return (
            <Modal
                title="文献库"
                open={modal.visible}
                onOk={handleOk}
                onCancel={modal.hide}
                width={800}
                maskClosable
                footer={[
                    showSelected
                        ? [
                              <Button key={'useDoc'} type={'default'} onClick={handleOk}>
                                  使用文献
                              </Button>,
                              <Button
                                  key="continueAdd"
                                  type="primary"
                                  onClick={() => setShowSelected(!showSelected)}
                              >
                                  继续添加
                              </Button>,
                          ]
                        : [
                              <Button key="cancel" onClick={modal.hide}>
                                  取消
                              </Button>,
                              <Button key="ok" type="primary" onClick={handleOk}>
                                  使用
                              </Button>,
                          ],
                ]}
            >
                {!showSelected && (
                    <>
                        {/* 指引区域 */}
                        <div className={'mb-4 flex items-center'}>
                            <span className={'text-[#192038]'}>自定义输入文献时，请使用</span>
                            <span className={'mr-2'}>查新引文格式</span>
                            <Popover
                                placement="rightTop"
                                trigger={isMobile ? 'click' : 'hover'}
                                content={
                                    <div className={'w-[500px] max-w-[90vw]'}>
                                        <img className={'mx-auto my-1 w-full'} src={Img1} />
                                        <img className={'mx-auto my-1 w-full'} src={Img2} />
                                    </div>
                                }
                                title="文献获取指引"
                            >
                                <InfoCircleOutlined
                                    style={{
                                        color: 'var(--colorPrimary)',
                                    }}
                                />
                            </Popover>
                        </div>

                        {/* 上传区域 */}
                        {!hasUploaded ? (
                            <div className={'mb-5'}>
                                <Upload.Dragger {...uploadProps} className="!bg-[#F8F9FB]">
                                    <div className="flex !h-[200px] flex-col items-center justify-center">
                                        <p className="mb-4">
                                            <img
                                                className="mx-auto h-12 w-12"
                                                src={uploadIcon}
                                                alt="upload"
                                            />
                                        </p>
                                        <p className="mb-2 text-base text-[#333333]">
                                            点击或拖拽文件到此区域上传
                                        </p>
                                        <p className="text-sm text-[#999999]">
                                            支持 .ris 格式，单个文件不超过 50MB
                                        </p>
                                    </div>
                                </Upload.Dragger>
                            </div>
                        ) : (
                            <div className={'mb-5 flex justify-end'}>
                                <Button type="primary" onClick={handleReUpload}>
                                    重新上传
                                </Button>
                            </div>
                        )}

                        {/* 文献列表 */}
                        <List
                            style={{
                                height: '600px',
                                overflow: 'auto',
                                scrollbarWidth: 'thin',
                            }}
                            loading={uploading}
                            dataSource={parsedReferences}
                            renderItem={(item) => (
                                <LiteratureItem
                                    item={item}
                                    onSelect={handleSelect}
                                    selected={selectedItems.some(
                                        (selected) => selected.id === item.id,
                                    )}
                                />
                            )}
                            locale={{
                                emptyText: null,
                            }}
                        />

                        {/* 查看文献按钮 */}
                        <div
                            className={
                                'absolute bottom-4 left-6 flex h-10 w-20 cursor-pointer items-center justify-center rounded-md bg-[#F8FBFF]'
                            }
                            onClick={() => setShowSelected(!showSelected)}
                        >
                            <Badge count={selectedItems?.length} showZero offset={[16, -2]}>
                                <div className={'text-[#8C93B1]'}>查看文献</div>
                            </Badge>
                        </div>
                    </>
                )}

                {/* 已选文献视图 */}
                {showSelected && (
                    <List
                        style={{
                            height: '600px',
                            overflow: 'auto',
                            scrollbarWidth: 'thin',
                        }}
                        header={<div>已选文献({selectedItems.length})篇</div>}
                        dataSource={selectedItems}
                        renderItem={(item) => (
                            <SelectedLiteratureItem
                                item={item}
                                onClose={() => handleRemove(item)}
                            />
                        )}
                        locale={{
                            emptyText: <Empty description="暂未选择文献" />,
                        }}
                    />
                )}
            </Modal>
        );
    }),
);

export default UploadRisModal;
